// CopilotKit GenUI PoC surface (/copilot).
//
// Renders the CopilotChat v2 against the backend AG-UI endpoint
// (/api/v1/copilotkit) and demonstrates GenUI: backend tool calls whose
// results are rendered as AntD components via useRenderTool. Agent logic,
// tools and persistence stay on the server — this page is render-only.
//
// Gated by the `copilotkit-lab` feature flag
// (VITE_FEATURE_COPILOTKIT_LAB=true).

import { CopilotChat, CopilotKitProvider, useRenderTool } from '@copilotkit/react-core/v2';
import '@copilotkit/react-core/v2/styles.css';
import { Card, Empty, List, Space, Spin, Tag, Typography } from 'antd';
import type { ReactElement } from 'react';
import { z } from 'zod';

import { useFeatureFlag } from '@govard/feature-flags/react';
import { useTranslation } from '@govard/i18n-core/react';

import { CRM_FLAG_IDS } from '../feature-flags/index.js';

const AGENT_ID = 'crm-assistant';

// Must mirror the show_hot_leads input schema from
// apps/crm/api/agents/tools/show-hot-leads.tool.ts.
const showHotLeadsParameters = z.object({
  limit: z.number().int().min(1).max(20).default(5),
  stages: z.array(z.enum(['New', 'Contacted', 'Negotiation', 'Won', 'Lost'])).optional(),
  minAmount: z.number().nonnegative().optional(),
});

// The bridge serialises tool output into the AG-UI tool result string; parse
// it back into a typed shape before rendering.
const hotLeadsResultSchema = z.object({
  leads: z.array(
    z.object({
      dealId: z.string(),
      title: z.string(),
      stage: z.string(),
      amount: z.number(),
      contactName: z.string().nullable(),
      reason: z.string(),
      score: z.number(),
    }),
  ),
});

function HotLeadsCard({ result }: { result: string }): ReactElement {
  const { t } = useTranslation();
  // JSON.parse throws on malformed strings; treat that as a failed parse.
  let leads: z.infer<typeof hotLeadsResultSchema>['leads'] | null = null;
  try {
    const parsed = hotLeadsResultSchema.safeParse(JSON.parse(result));
    if (parsed.success) leads = parsed.data.leads;
  } catch {
    leads = null;
  }
  if (leads === null) {
    return (
      <Card size="small" title={t('crm.copilot.hot_leads')}>
        <Typography.Paragraph type="warning">{t('crm.copilot.parse_error')}</Typography.Paragraph>
        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{result}</pre>
      </Card>
    );
  }
  return (
    <Card size="small" title={t('crm.copilot.hot_leads')}>
      <List
        size="small"
        dataSource={leads}
        locale={{ emptyText: t('crm.copilot.no_leads') }}
        renderItem={(lead) => (
          <List.Item key={lead.dealId}>
            <Space orientation="vertical" size={0}>
              <Space>
                <Typography.Text strong>{lead.title}</Typography.Text>
                <Tag>{lead.stage}</Tag>
              </Space>
              <Typography.Text type="secondary">
                {lead.contactName ?? '—'} · {lead.amount.toLocaleString('ru-RU')} · score{' '}
                {lead.score} · {lead.reason}
              </Typography.Text>
            </Space>
          </List.Item>
        )}
      />
    </Card>
  );
}

function GenUiRenderers(): null {
  useRenderTool({
    name: 'show_hot_leads',
    parameters: showHotLeadsParameters,
    agentId: AGENT_ID,
    render: (props) =>
      props.status === 'complete' ? <HotLeadsCard result={props.result} /> : <Spin size="small" />,
  });
  return null;
}

export function CopilotLabPage(): ReactElement {
  const { t } = useTranslation();
  const enabled = useFeatureFlag(CRM_FLAG_IDS.COPILOTKIT_LAB);
  if (!enabled) {
    return (
      <Empty
        description={
          <>
            {t('crm.copilot.flag_off')}{' '}
            <Typography.Text code>VITE_FEATURE_COPILOTKIT_LAB=true</Typography.Text>{' '}
            {t('crm.copilot.flag_off_suffix')}
          </>
        }
      />
    );
  }
  return (
    <CopilotKitProvider runtimeUrl="/api/v1/copilotkit" credentials="include">
      <GenUiRenderers />
      <div style={{ height: 'calc(100vh - 140px)' }}>
        <CopilotChat agentId={AGENT_ID} />
      </div>
    </CopilotKitProvider>
  );
}
